"use client"

import { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { library } from "@fortawesome/fontawesome-svg-core"
import * as solidIcons from "@fortawesome/free-solid-svg-icons"
import * as regularIcons from "@fortawesome/free-regular-svg-icons"
import * as brandIcons from "@fortawesome/free-brands-svg-icons"
import { SVG_CHEVRON_DOWN, SVG_CHEVRON_UP } from "../../../Assets/SVG/svg_util"
import TextField from "./TextField"

// Add all icons to the library
library.add(solidIcons.fas, regularIcons.far, brandIcons.fab)

const IconSelector = ({ onSelect, initialIcon, label = "Icon" }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIcon, setSelectedIcon] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const dropdownRef = useRef(null)

  const allIcons = Object.keys(solidIcons)
    .filter((key) => key !== "fas" && key !== "prefix")
    .map((name) => ({ iconName: name, icon: solidIcons[name] }))
    .concat(
      Object.keys(regularIcons)
        .filter((key) => key !== "far" && key !== "prefix")
        .map((name) => ({ iconName: name, icon: regularIcons[name] })),
    )
    .concat(
      Object.keys(brandIcons)
        .filter((key) => key !== "fab" && key !== "prefix")
        .map((name) => ({ iconName: name, icon: brandIcons[name] })),
    )

  useEffect(() => {
    if (initialIcon) {
      const foundIcon = allIcons.find((icon) => icon.iconName === initialIcon)
      if (foundIcon) {
        setSelectedIcon(foundIcon)
      }
    }
  }, [initialIcon, allIcons])

  const filteredIcons = allIcons.filter((item) => item.iconName.toLowerCase().includes(searchTerm.toLowerCase()))

  const handleIconSelect = (item) => {
    setSelectedIcon(item)
    setIsOpen(false)
    onSelect(item.iconName)
  }

  const handleClearSelection = (e) => {
    e.stopPropagation()
    setSelectedIcon(null)
    onSelect(null)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="relative inline-block text-left w-full" ref={dropdownRef}>
      <div className="py-1">
        <label className="text-start font-roboto font-[400] text-[14px]">{label}</label>
      </div>
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between px-2 py-[5px] ${
          isOpen ? "border-[#7ddbff]" : "border-gray-300"
        } rounded cursor-pointer border-2 !bg-[#7ddbff] !bg-opacity-[30%]`}
      >
        <span>
          {selectedIcon ? (
            <>
              <FontAwesomeIcon icon={selectedIcon.icon} />
              {/* <span className="ml-2">{selectedIcon.iconName}</span> */}
            </>
          ) : (
            "Select Icon"
          )}
        </span>

        <div className="flex items-center">
          {selectedIcon && (
            <button onClick={handleClearSelection} className="mr-2 text-gray-500 hover:text-gray-700">
              &#x2715;
            </button>
          )}
          {isOpen ? <SVG_CHEVRON_UP /> : <SVG_CHEVRON_DOWN />}
        </div>
      </div>

      {isOpen && (
        <div className="absolute mt-2 w-full bg-white border border-gray-300 rounded shadow-lg p-4 max-h-80 overflow-auto z-10">
          <div className="sticky -top-4 bg-white">
            <TextField
              placeholder="Search icons..."
              value={searchTerm}
              onValueChange={(_, value) => setSearchTerm(value)}
              style={{
                padding: "5px",
                border: "1px solid gray",
                marginBottom: "10px",
                width: "100%",
              }}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {filteredIcons.map((item, idx) => (
              <div
                key={idx}
                className={`icon-item flex flex-col group items-center p-2 border rounded cursor-pointer ${
                  selectedIcon && selectedIcon.iconName === item.iconName ? "bg-blue-200" : ""
                }`}
                onClick={() => handleIconSelect(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span className="opacity-0 group-hover:opacity-80 text-xs mt-1 whitespace-normal">{item.iconName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default IconSelector

